import _defineProperty from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * LCSR v1.8 Field Validation Rules
 *
 * This file contains hardcoded validation rules from the LCSR specification
 * for fields where the API may return null or incorrect min/max values.
 *
 * These rules override API-provided metadata to ensure compliance with ACR requirements.
 *
 * Reference: lcsr.py (Pydantic v2 models for LCSR v1.8 transaction validation)
 */

/**
 * LCSR field validation rules registry
 * Key: exact field_name as it appears in LCSR spec (case-sensitive)
 */
var LCSR_FIELD_VALIDATION_RULES = {
  // Patient Demographics
  Patient_Height: {
    minValue: 0.01,
    maxValue: 98.99,
    step: 0.01,
    decimalPlaces: 2,
    formatHint: "Height in inches. Must be between 0.01 and 98.99 inches. Leave empty if unknown or not documented"
  },
  Patient_Weight: {
    minValue: 0.01,
    maxValue: 998.99,
    step: 0.01,
    decimalPlaces: 2,
    formatHint: "Weight in lbs. Must be between 0.01 and 998.99 lbs. Leave empty if unknown or not documented"
  },
  // Smoking History
  Number_Of_Packs_Year_Smoking: {
    minValue: 0.1,
    maxValue: 999.9,
    step: 0.1,
    decimalPlaces: 1,
    formatHint: "Pack-years must be between 0.1 and 999.9"
  },
  Number_Of_Years_Since_Quit: {
    minValue: 0.01,
    maxValue: 99.99,
    step: 0.01,
    decimalPlaces: 2,
    formatHint: "Years since quit must be between 0.01 and 99.99"
  },
  // Scanner & Technical Parameters
  Tube_Current_Time: {
    minValue: 0,
    maxValue: 999.9,
    step: 0.1,
    decimalPlaces: 1,
    formatHint: "Tube current-time (mAs) must be between 0 and 999.9"
  },
  Tube_Voltage: {
    minValue: 0,
    maxValue: 300,
    step: 1,
    decimalPlaces: 0,
    formatHint: "Tube voltage (kV) must be between 0 and 300"
  },
  Scanning_Time: {
    minValue: 0.01,
    maxValue: 999.99,
    step: 0.01,
    decimalPlaces: 2,
    formatHint: "Scanning time (seconds) must be between 0.01 and 999.99"
  },
  Scanning_Volume: {
    minValue: 0.01,
    maxValue: 999.99,
    step: 0.01,
    decimalPlaces: 2,
    formatHint: "Scanning volume (cm) must be between 0.01 and 999.99"
  },
  Pitch: {
    minValue: 0,
    maxValue: 99.999,
    step: 0.001,
    decimalPlaces: 3,
    formatHint: "Pitch must be between 0 and 99.999"
  },
  Reconstructed_Image_Width: {
    minValue: 0,
    maxValue: 9.99,
    step: 0.01,
    decimalPlaces: 2,
    formatHint: "Must be 0 (Unknown) or a value between 0.01 and 9.99 mm with max 2 decimal places"
  },
  // Additional numeric fields with validation
  CTDIvol: {
    minValue: 0,
    maxValue: 999.9,
    step: 0.1,
    decimalPlaces: 1,
    formatHint: "CTDIvol must be a numeric value"
  },
  DLP: {
    minValue: 0,
    maxValue: 9999.9,
    step: 0.1,
    decimalPlaces: 1,
    formatHint: "DLP must be a numeric value"
  },
  Years_Since_Prior_Diagnosis: {
    minValue: 0,
    maxValue: 99,
    step: 1,
    decimalPlaces: 0,
    formatHint: "Must be an integer value"
  }
};

/**
 * Get hardcoded validation rules for a specific field
 *
 * @param fieldName - The exact field name from LCSR spec
 * @returns FieldValidationRule if found, undefined otherwise
 */
export function getLCSRFieldValidationRule(fieldName) {
  return LCSR_FIELD_VALIDATION_RULES[fieldName];
}

/**
 * Apply LCSR validation rules to field metadata, overriding API values when they are null or incorrect
 *
 * @param fieldName - The field name
 * @param apiMinValue - Min value from API (may be null)
 * @param apiMaxValue - Max value from API (may be null)
 * @param apiFormatHint - Format hint from API (may be null)
 * @returns Object with validated min/max values and format hint
 */
export function applyLCSRValidationRules(fieldName, apiMinValue, apiMaxValue, apiFormatHint) {
  var lcsrRule = getLCSRFieldValidationRule(fieldName);
  if (!lcsrRule) {
    // No hardcoded rule for this field, use API values as-is
    return {
      minValue: apiMinValue !== null && apiMinValue !== void 0 ? apiMinValue : undefined,
      maxValue: apiMaxValue !== null && apiMaxValue !== void 0 ? apiMaxValue : undefined,
      formatHint: apiFormatHint !== null && apiFormatHint !== void 0 ? apiFormatHint : undefined
    };
  }

  // Override null/missing API values with LCSR rules
  // Always prefer LCSR formatHint as it includes detailed range information
  return {
    minValue: apiMinValue !== null && apiMinValue !== void 0 ? apiMinValue : lcsrRule.minValue,
    maxValue: apiMaxValue !== null && apiMaxValue !== void 0 ? apiMaxValue : lcsrRule.maxValue,
    formatHint: lcsrRule.formatHint || apiFormatHint || undefined
  };
}

/**
 * Get step value for numeric inputs based on decimal places
 *
 * @param fieldName - The field name
 * @returns Step value (e.g., 0.01 for 2 decimal places, 1 for integers)
 */
export function getLCSRFieldStep(fieldName) {
  var lcsrRule = getLCSRFieldValidationRule(fieldName);
  return lcsrRule === null || lcsrRule === void 0 ? void 0 : lcsrRule.step;
}

/**
 * Get maximum decimal places allowed for a field
 *
 * @param fieldName - The field name
 * @returns Number of decimal places, or undefined if not specified
 */
export function getLCSRFieldDecimalPlaces(fieldName) {
  var lcsrRule = getLCSRFieldValidationRule(fieldName);
  return lcsrRule === null || lcsrRule === void 0 ? void 0 : lcsrRule.decimalPlaces;
}

/**
 * Apply LCSR validation rules to all field schemas in an array.
 * This ensures that validation functions like validateField have access to
 * the correct min/max values even when the API returns null.
 *
 * @param schemas - Array of field schemas from API
 * @returns Array of field schemas with LCSR rules applied to metadata
 */
export function applyLCSRValidationRulesToSchemas(schemas) {
  return schemas.map(function (schema) {
    var _ref, _schema$metadata$min_, _schema$metadata, _ref2, _schema$metadata$max_, _schema$metadata2, _schema$metadata3;
    var lcsrRule = getLCSRFieldValidationRule(schema.field_name);

    // If no LCSR rule exists for this field, return schema as-is
    if (!lcsrRule) {
      return schema;
    }

    // Apply LCSR rules to metadata, overriding null API values
    return _objectSpread(_objectSpread({}, schema), {}, {
      metadata: _objectSpread(_objectSpread({}, schema.metadata), {}, {
        min_value: (_ref = (_schema$metadata$min_ = (_schema$metadata = schema.metadata) === null || _schema$metadata === void 0 ? void 0 : _schema$metadata.min_value) !== null && _schema$metadata$min_ !== void 0 ? _schema$metadata$min_ : lcsrRule.minValue) !== null && _ref !== void 0 ? _ref : null,
        max_value: (_ref2 = (_schema$metadata$max_ = (_schema$metadata2 = schema.metadata) === null || _schema$metadata2 === void 0 ? void 0 : _schema$metadata2.max_value) !== null && _schema$metadata$max_ !== void 0 ? _schema$metadata$max_ : lcsrRule.maxValue) !== null && _ref2 !== void 0 ? _ref2 : null,
        format_hint: lcsrRule.formatHint || ((_schema$metadata3 = schema.metadata) === null || _schema$metadata3 === void 0 ? void 0 : _schema$metadata3.format_hint) || null
      })
    });
  });
}